import * as React from 'react';
import { CheckMarkIcon, CreditCardIcon, renderWaitingForSuccessElement } from '../../../webportal/components/common/Icons';
import { createDefaultApi } from '../../../webportal/utils/createApi';
import { BackOfficeEvent } from '../common/BackOfficeEvents';
import { EventManager } from '../../../webportal/services/EventManager';

export interface ConfirmModalProps {
    isUserSuspended: boolean;
    username: string;
    userId: string;
    isModalShown: boolean;
    onSuccess: () => Promise<void>;
    numberOfFilespaces?: number;
    totalFilespaceSize?: string;
}

enum ModalStatusState {
    SuspendNotStarted,
    SuspensionInProcess = 'Suspending in process',
    SuspensionSuccess = 'Suspension was successful',
    SuspensionFailed = 'Suspension failed. Please try again or contact our technical team.',
    UnsuspendNotStarted = '',
    UnsuspendInProcess = 'Unsuspending user in progress',
    UnsuspensionSuccess = 'Unsuspension was successful',
    UnsuspensionFailed = 'Unsuspension failed. Please try again or contact our technical team.'
}

interface ConfirmModalState {
    username: string;
    usernamesMatch: boolean;
    modalState?: ModalStatusState;
    errorMessage: string;
}

export class UserStatusChangeConfirmModal extends React.Component<ConfirmModalProps, ConfirmModalState> {
    constructor(props: ConfirmModalProps) {
        super(props);
        this.processHandler = this.processHandler.bind(this);
        this.suspendUser = this.suspendUser.bind(this);
        this.unsuspendUser = this.unsuspendUser.bind(this);
        this.handleUserInputChanged = this.handleUserInputChanged.bind(this);
        this.resetModalState = this.resetModalState.bind(this);
        this.renderSuccessElement = this.renderSuccessElement.bind(this);
        this.renderOperationFailedElement = this.renderOperationFailedElement.bind(this);
        this.renderSuspendButtonElement = this.renderSuspendButtonElement.bind(this);
        this.removeErrorComponent = this.removeErrorComponent.bind(this);
        this.onEnterPressed = this.onEnterPressed.bind(this);
        this.state = {
            username: '',
            usernamesMatch: false,
            errorMessage: ''
        };
    }

    componentDidMount() {
        if (this.props.isUserSuspended) {
            this.setState({modalState: ModalStatusState.UnsuspendNotStarted});
        } else {
            this.setState({modalState: ModalStatusState.SuspendNotStarted});
        }
    }
    render() {
        const modalClassName = this.props.isModalShown ? 'modalBO show' : 'modalBO';
        return (
            <div className={modalClassName} id="confirmModal" tabIndex={-1} role="dialog" aria-labelledby="confirmModal" aria-hidden="true">
                <div>
                    <header className="center">
                        <CreditCardIcon className="icon" width="27px"/>
                        {this.props.isUserSuspended && <h2>Confirm unsuspend of {this.props.username}</h2>}
                        {!this.props.isUserSuspended && <h2>Confirm suspension of {this.props.username}</h2>}
                        {!this.props.isUserSuspended && this.props.numberOfFilespaces !== 0 && this.props.totalFilespaceSize &&
                        <h3>You are about to suspend {this.props.numberOfFilespaces} Filespaces with total
                            size {this.props.totalFilespaceSize}</h3>}
                        {this.props.isUserSuspended && this.props.numberOfFilespaces !== 0 && this.props.totalFilespaceSize &&
                        <h3>You are about to unsuspend {this.props.numberOfFilespaces} Filespaces with total
                            size {this.props.totalFilespaceSize} </h3>}
                        {this.props.isUserSuspended && this.props.numberOfFilespaces !== 0 && !this.props.totalFilespaceSize &&
                         <h3>You are about to unsuspend {this.props.numberOfFilespaces} Filespaces</h3>}
                        {!this.props.isUserSuspended && this.props.numberOfFilespaces !== 0 && !this.props.totalFilespaceSize &&
                         <h3>You are about to suspend {this.props.numberOfFilespaces} Filespaces</h3>}
                    </header>
                    <section>
                        <p>Please confirm the email of the user.</p>
                        <input
                            onKeyPress={async (event) => {
                            await this.onEnterPressed(event);
                            }}
                            data-autocomplete="off"
                            type="text"
                            name="filespace"
                            id="file-system-name"
                            value={this.state.username}
                            onChange={this.handleUserInputChanged}
                        />
                    </section>
                    {this.processHandler(this.state.modalState)}
                    {this.state.errorMessage &&
                    <a
                        onClick={this.resetModalState}
                        data-dismiss="modal"
                        className="close"
                        aria-label="close"
                        href="javascript:void(0)"
                    >
                        Cancel
                    </a>}
                </div>
            </div>
        );
    }

    private async onEnterPressed(event: any): Promise<void> {
        if (event.key === 'Enter' && this.state.usernamesMatch && this.state.modalState === ModalStatusState.SuspendNotStarted) {
            await this.suspendUser();
        }
        if (event.key === 'Enter' && this.state.usernamesMatch && this.state.modalState === ModalStatusState.UnsuspendNotStarted) {
            await this.unsuspendUser();
        }
        return;
    }

    private processHandler(modalState: ModalStatusState | undefined): JSX.Element | undefined {
        switch (modalState) {
            case ModalStatusState.SuspendNotStarted:
                return this.renderSuspendButtonElement();
            case ModalStatusState.UnsuspendNotStarted:
                return this.renderUnsuspendButtonElement();
            case ModalStatusState.SuspensionFailed:
            case ModalStatusState.UnsuspensionFailed:
                return this.renderOperationFailedElement();
            case ModalStatusState.SuspensionInProcess:
            case ModalStatusState.UnsuspendInProcess:
                return renderWaitingForSuccessElement('Processing your request...');
            case ModalStatusState.SuspensionSuccess:
            case ModalStatusState.UnsuspensionSuccess:
                return this.renderSuccessElement();
            default:
                return undefined;
        }
    }

    private renderSuspendButtonElement(): JSX.Element {
        return (
            <footer>
                <button
                    className={this.state.usernamesMatch ? 'btn warning' : 'btn warning disable'}
                    disabled={!this.state.usernamesMatch}
                    onClick={this.suspendUser}
                >
                    Suspend
                </button>
                <a
                    onClick={this.resetModalState}
                    data-dismiss="modal"
                    className="close"
                    aria-label="close"
                    href="javascript:void(0)"
                >
                    Cancel
                </a>
            </footer>
        );
    }

    private renderUnsuspendButtonElement(): JSX.Element {
        return (
            <footer>
                <div>
                    <button
                        className={this.state.usernamesMatch ? 'btn warning' : 'btn warning disable'}
                        disabled={!this.state.usernamesMatch}
                        onClick={this.unsuspendUser}
                    >
                        Unsuspend
                    </button>
                </div>
                <a onClick={this.resetModalState} data-dismiss="modal" href="javascript:void(0)">Cancel</a>
            </footer>
        );
    }

    private async suspendUser(): Promise<void> {
        try {
            this.setState({modalState: ModalStatusState.SuspensionInProcess});
            const api = await createDefaultApi();
            await api.suspendUser(this.props.userId);
            this.setState({modalState: ModalStatusState.SuspensionSuccess});
            setTimeout(async () => { await this.props.onSuccess(); }, 2000);
        } catch (e) {
            this.setState(
                {
                    errorMessage: 'Failed to suspend. Please try again later or contact the development team.',
                    modalState: ModalStatusState.SuspensionFailed
                });
        }
    }

    private async unsuspendUser(): Promise<void> {
        try {
            this.setState({modalState: ModalStatusState.UnsuspendInProcess});
            const api = await createDefaultApi();
            await api.unsuspendUser(this.props.userId);
            this.setState({modalState: ModalStatusState.UnsuspensionSuccess});
            setTimeout(async () => { await this.props.onSuccess(); }, 2000);
        } catch (e) {
            this.setState(
                {
                    errorMessage: 'Failed to unsuspend. Please try again later or contact the development team.',
                    modalState: ModalStatusState.UnsuspensionFailed
                });
        }
    }

    private handleUserInputChanged(e: any): void {
        const trimmedValue = e.target.value.trim();
        this.setState(
            {
                          username: trimmedValue,
                          usernamesMatch: trimmedValue === this.props.username
                      });
    }

    private renderSuccessElement(): JSX.Element {
        return (
            <footer>
                <div className="icon">
                    <CheckMarkIcon width="28px" height="20px"/>
                </div>
                <span className="success">
                    Done
                </span>
                <a onClick={this.resetModalState} data-dismiss="modal" href="javascript:void(0)">Close</a>
            </footer>
        );
    }

    private renderOperationFailedElement(): JSX.Element {
        return (
            <footer className="warning">
                <span>
                   {this.state.errorMessage}
                </span>
            </footer>
        );
    }

    private resetModalState(): void {
        if (this.props.isUserSuspended) {
            this.setState(
                {
                    modalState: ModalStatusState.UnsuspendNotStarted,
                    username: '',
                    usernamesMatch: false
                });
            EventManager.fireCustomEvent(BackOfficeEvent.CloseModal);
            return;
        }
        if (!this.props.isUserSuspended) {
            this.setState(
                {
                    modalState: ModalStatusState.SuspendNotStarted,
                    username: '',
                    usernamesMatch: false
                });
            EventManager.fireCustomEvent(BackOfficeEvent.CloseModal);
            return;
        }

    }

    private removeErrorComponent(): void {
        setTimeout(
            () => {
                this.setState({errorMessage: ''});
            },
            5000);
    }

}



// WEBPACK FOOTER //
// ./src/backoffice/components/user/UserStatusChangeConfirmModal.tsx